<script setup lang="ts">
/**
 * カテゴリ(右ゾーン)の左端に置くリサイズハンドル。
 *
 * 左へドラッグするとカテゴリが広がり、その分だけ商品属性側が狭くなる。
 * 逆に右へドラッグすれば商品属性が広がる。
 * 対比ゾーン全体に対する「割合(%)」で保持するため、画面幅が変わっても
 * 比率が保たれ、狭いモニタで開いても画面からはみ出さない。
 *
 * PaneDivider と同じく pointerイベント + setPointerCapture を使うことで、
 * ドラッグ中にカーソルが他要素の上へ抜けても追従が途切れない。
 */
import { ref } from 'vue'
import { useLayoutStore } from '@/stores/layout'

const layout = useLayoutStore()
const dragging = ref(false)

/** 対比ゾーン(.detail-compare)の矩形。割合の算出に使う */
function compareRect(el: HTMLElement): DOMRect | null {
  return el.closest('.detail-compare')?.getBoundingClientRect() ?? null
}

function onPointerDown(e: PointerEvent) {
  dragging.value = true
  ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
  document.body.style.userSelect = 'none'
  document.body.style.cursor = 'col-resize'
}

function onPointerMove(e: PointerEvent) {
  if (!dragging.value) return
  const rect = compareRect(e.currentTarget as HTMLElement)
  if (!rect || rect.width === 0) return
  // カーソルより右側が画像エリアになる
  const ratio = ((rect.right - e.clientX) / rect.width) * 100
  layout.setPreviewRatio(ratio)
}

function onPointerUp(e: PointerEvent) {
  if (!dragging.value) return
  dragging.value = false
  ;(e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId)
  document.body.style.userSelect = ''
  document.body.style.cursor = ''
  layout.persist()
}

/** ダブルクリックで既定の割合に戻す */
function onDoubleClick() {
  layout.resetPreviewRatio()
}
</script>

<template>
  <div
    class="preview-resizer"
    :class="{ dragging }"
    title="ドラッグで商品属性・カテゴリの幅を調整 / ダブルクリックで既定に戻す"
    role="separator"
    aria-orientation="vertical"
    @pointerdown="onPointerDown"
    @pointermove="onPointerMove"
    @pointerup="onPointerUp"
    @pointercancel="onPointerUp"
    @dblclick="onDoubleClick"
  >
    <span class="preview-resizer-grip"></span>
  </div>
</template>
